import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { logIn, signUp } from '../services/firebase/auth';
import { updateLastLogin, getClimber, createClimber } from '../services/firebase/climber';
import SEO from '../components/SEO';
import '../styles/Auth.css';
import bananaWelcomeImage from '../assets/images/banana_welcome_desktop.webp';
import bananaGroupImage from '../assets/images/banana_group.webp';
import bananaSignupImage from '../assets/images/banana_signup.webp';

// SignupForm Component for Desktop
const SignupFormDesktop: React.FC<{onBackToLogin: () => void, language: string}> = ({ onBackToLogin, language }) => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      
      // Create the user in Firebase Authentication
      const user = await signUp(email, password, displayName);
      
      // Create a Climber profile in Firestore
      if (user && user.uid) {
        await createClimber(user.uid, displayName, displayName, language);
        
        // Redirect to gyms page after signup
        navigate('/gyms');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError('Failed to create account: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-card">
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="displayName">Username</label>
          <input 
            type="text" 
            id="displayName" 
            value={displayName} 
            onChange={(e) => setDisplayName(e.target.value)} 
            required 
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input 
            type="email" 
            id="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            required 
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-container">
            <input 
              type={showPassword ? "text" : "password"} 
              id="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
            />
            <button 
              type="button" 
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>
        </div>
        <button disabled={loading} className="auth-button" type="submit">
          {loading ? 'Creating Account...' : 'Sign Up'}
        </button>
      </form>
      <div className="auth-links">
        <p>
          Already have an account? <a href="#" onClick={(e) => {
            e.preventDefault();
            onBackToLogin();
          }}>Log In</a>
        </p>
      </div>
    </div>
  );
};

// Desktop Login Component
const LoginDesktop: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('EN'); // Default to English
  const [currentPage, setCurrentPage] = useState(0); // Carousel page index
  const navigate = useNavigate();
  
  // Carousel pages configuration
  const carouselPages = [
    {
      id: 'welcome',
      backgroundImage: bananaWelcomeImage
    },
    {
      id: 'credits',
      backgroundImage: bananaGroupImage
    },
    {
      id: 'auth',
      backgroundImage: bananaSignupImage // Use signup image for auth page
    }
  ];

  // Auth form state
  const [showSignupForm, setShowSignupForm] = useState(true); // Default to signup form

  // Login form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      const user = await logIn(email, password);
      
      // First check if the user's email is verified
      if (user && !user.emailVerified) {
        // Check if user is an existing user (created more than 1 day ago)
        if (user.metadata && user.metadata.creationTime) {
          const creationTime = new Date(user.metadata.creationTime);
          const currentDate = new Date();
          const oneDayInMs = 24 * 60 * 60 * 1000;
          
          // For existing users, don't require email verification
          if (currentDate.getTime() - creationTime.getTime() <= oneDayInMs) {
            // Only new users (less than 1 day old) need to verify email
            navigate('/confirm-email');
            return;
          }
        } else {
          // If we can't determine creation time, default to verification
          navigate('/confirm-email');
          return;
        }
      }
      
      // If email is verified or user is an existing user, update the last login timestamp in Firestore
      if (user && user.uid) {
        await updateLastLogin(user.uid);
        
        // Get the user's climber profile
        const climber = await getClimber(user.uid);
        
        // Get gym IDs from climber profile
        const gymIds = climber?.getGymIds() || [];
        
        if (gymIds.length > 0) {
          // User has at least one gym, redirect to the first gym's detail page
          const firstGymId = gymIds[0];
          navigate(`/gyms/${firstGymId}`);
        } else {
          // User has no gyms, redirect to the gyms list
          navigate('/gyms');
        }
      } else {
        // Fallback to home page if user data is incomplete
        navigate('/');
      }
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential') {
        setError('We couldn\'t find an account with those credentials. Please check your email and password and try again.');
      } else {
        setError('Failed to log in: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Navigation functions
  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % carouselPages.length);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + carouselPages.length) % carouselPages.length);
  };

  const goToPage = (pageIndex: number) => {
    setCurrentPage(pageIndex);
  };

  // Render welcome page content
  const renderWelcomePage = () => {
    // Get version from meta tag
    let version = '';
    if (typeof document !== 'undefined') {
      const meta = document.querySelector('meta[name="app-version"]');
      if (meta) version = meta.getAttribute('content') || '';
    }
    return (
      <div className="carousel-content welcome-content">
        <div className="welcome-version-number">{version && (
          <span>{version}</span>
        )}</div>
        <div className="welcome-text">
          <h2>Welcome to the</h2>
          <p>Banana Cup</p>
        </div>
      </div>
    );
  };

  // Render credits page content
  const renderCreditsPage = () => (
    <div className="carousel-content credits-content">
      <div className="credits-text">
        <h2>Created by Fré Leys for</h2>
        <p>Paraclimbing.be</p>
      </div>
    </div>
  );

  // Render auth page content
  const renderAuthPage = () => (
    <div className="carousel-content auth-content">
      {showSignupForm ? (
        <SignupFormDesktop 
          onBackToLogin={() => setShowSignupForm(false)} 
          language={language} 
        />
      ) : (
        <div className="auth-card">
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input 
                  type={showPassword ? "text" : "password"} 
                  id="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>
            <button disabled={loading} className="auth-button" type="submit">
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
          <div className="auth-links">
            <p>
              Need an account? <a href="#" onClick={(e) => {
                e.preventDefault();
                setShowSignupForm(true);
              }}>Sign Up</a>
            </p>
            <p>
              <Link to="/forgot-password">Forgot Password?</Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <SEO 
        title="Login - Banana Cup Climbing | Climbing Logger with Heart Rate Monitor"
        description="Login to Banana Cup climbing app to track your climbing progress, log routes, and connect with the paraclimbing community. Access your climbing data and heart rate monitor integration."
        keywords="login climbing app, banana cup login, climbing app login, paraclimbing login, bananacup.be login, paraklimmen login"
        path="/login"
      />
      <div className={`carousel-container desktop-view ${currentPage === 2 ? 'auth-page' : ''}`}>
        <div 
          className="carousel-page"
          style={{
            // Apply background to entire page for mobile compatibility
            backgroundImage: `url(${carouselPages[currentPage].backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Image section for desktop only */}
          <div 
            className="carousel-image-section"
            style={{
              backgroundImage: `url(${carouselPages[currentPage].backgroundImage})`
            }}
          />
          
          {/* Content section for desktop */}
          <div className="carousel-content-section">
            <div className="carousel-content">
              {/* Page content */}
              {currentPage === 0 && renderWelcomePage()}
              {currentPage === 1 && renderCreditsPage()}
              {currentPage === 2 && renderAuthPage()}
            </div>
          </div>

          {/* Back button for auth page - positioned over entire screen */}
          {currentPage === 2 && (
            <button 
              className="auth-back-button desktop-back-button screen-back-button"
              onClick={() => goToPage(0)}
              aria-label="Back to welcome"
            >
              ← Back
            </button>
          )}

          {/* Mobile content (shows when desktop sections are hidden) */}
          <div className="carousel-content mobile-content">
            {/* Page content for mobile */}
            {currentPage === 0 && renderWelcomePage()}
            {currentPage === 1 && renderCreditsPage()}
            {currentPage === 2 && renderAuthPage()}
          </div>

          {/* Background overlay for mobile (kept for mobile compatibility) */}
          <div className="carousel-overlay"></div>

          {/* Navigation controls */}
          <div className="carousel-navigation">
            {/* Left arrow */}
            <button 
              className="nav-arrow prev-arrow"
              onClick={prevPage}
              aria-label="Previous page"
              disabled={currentPage === 0}
            >
              ‹
            </button>
            
            {/* Page indicators */}
            <div className="page-indicators">
              {carouselPages.map((_, index) => (
                <button
                  key={index}
                  className={`page-indicator ${currentPage === index ? 'active' : ''}`}
                  onClick={() => goToPage(index)}
                  aria-label={`Go to page ${index + 1}`}
                />
              ))}
            </div>

            {/* Right arrow */}
            <button 
              className="nav-arrow next-arrow"
              onClick={nextPage}
              aria-label="Next page"
              disabled={currentPage === carouselPages.length - 1}
            >
              ›
            </button>
          </div>
        </div>
        
        {/* Language selector at bottom of screen */}
        <div className="language-selector">
          <select 
            id="language-select" 
            value={language} 
            onChange={(e) => setLanguage(e.target.value)}
            className="language-dropdown"
          >
            <option value="EN">English</option>
            <option value="NL">Nederlands</option>
            <option value="FR">Français</option>
          </select>
        </div>
      </div>
    </>
  );
};

export default LoginDesktop;
